import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "./Reveal";
import { bookService } from "./Nav";

const SOCIAL_IMG =
    "https://images.unsplash.com/photo-1611926653458-09294b3142bf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHw0fHxzb2NpYWwlMjBtZWRpYSUyMG1hcmtldGluZyUyMHBob25lfGVufDB8fHx8MTc4NjgxNzc5Mnww&ixlib=rb-4.1.0&q=85&w=1200";

const DEV = [
    {
        n: "01",
        sid: "web-dev",
        title: "Website & Web App Development",
        desc: "A real website or custom web application for your business — designed, built, and launched for you. Booking tools, customer portals, e-commerce, internal dashboards. You bring the vision, I ship the software.",
        outcome: "Starts with a free discovery call — in person or virtual.",
        accent: true,
    },
    {
        n: "02",
        sid: "crm-dev",
        title: "Custom CRM & Business Systems",
        desc: "Secure, custom CRM platforms designed and built around your exact workflow — automating the busywork and scaling as your business grows. Ideal for startups and growing companies that need custom software, not off-the-shelf compromises.",
        outcome: "Free discovery call — custom-quoted after we talk.",
    },
];

const CLASSES = [
    {
        n: "03",
        sid: "social-content",
        title: "Social Media Content with AI",
        desc: "Plan, write, and design posts with the right AI tools — in your voice, for your audience.",
        outcome: "Leave with a month of content planned and drafted.",
        img: SOCIAL_IMG,
    },
    {
        n: "04",
        sid: "workflow-automation",
        title: "Workflow Automation",
        desc: "Connect the tools you already use so the busywork disappears.",
        outcome: "Leave with at least one working automation.",
    },
    {
        n: "05",
        sid: "email-automation",
        title: "Email Automation",
        desc: "Follow-ups, newsletters, and drip campaigns that run while you sleep.",
        outcome: "Leave with your first sequence live.",
    },
    {
        n: "06",
        sid: "web-building",
        title: "Build-Your-Own-Website Class",
        desc: "Learn to ship a real site or a simple app yourself — no computer science degree required.",
        outcome: "Leave with something live on the internet.",
    },
];

const DevCard = ({ s }) => (
    <article
        data-testid={`service-card-${s.n}`}
        className={`group p-8 md:p-12 h-full flex flex-col transition-colors duration-300 ${
            s.accent ? "bg-worx text-white" : "bg-ink hover:bg-[#141414]"
        }`}
    >
        <span className={`font-display text-lg ${s.accent ? "text-white/80" : "text-worx"}`}>{s.n}</span>
        <h3 className="mt-3 font-display font-medium tracking-tight text-2xl md:text-4xl">{s.title}</h3>
        <p className={`mt-4 leading-relaxed ${s.accent ? "text-white/85" : "text-paper/60"}`}>{s.desc}</p>
        <p className={`mt-4 text-sm italic ${s.accent ? "text-white/60" : "text-paper/40"}`}>{s.outcome}</p>
        <a
            href="#booking"
            onClick={bookService(s.sid)}
            data-testid={`service-book-${s.n}`}
            className={`mt-auto pt-8 inline-flex items-center gap-1.5 text-sm w-max transition-colors duration-200 ${
                s.accent ? "text-white hover:text-ink" : "text-paper/70 hover:text-worx"
            }`}
        >
            Book a free dev consult <ArrowUpRight size={14} />
        </a>
        {s.sid === "web-dev" && (
            <Link
                to="/services/web-development"
                data-testid="service-more-01"
                className="mt-3 inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-ink transition-colors duration-200 w-max"
            >
                See packages & pricing <ArrowUpRight size={14} />
            </Link>
        )}
    </article>
);

const ClassCard = ({ s }) => (
    <article
        data-testid={`service-card-${s.n}`}
        className="group bg-ink p-6 md:p-8 h-full flex flex-col hover:bg-[#141414] transition-colors duration-300"
    >
        {s.img && (
            <div className="overflow-hidden mb-6">
                <img
                    src={s.img}
                    alt={s.title}
                    className="w-full h-36 object-cover grayscale-[30%] group-hover:grayscale-0 group-hover:scale-[1.03] transition-[transform,filter] duration-500"
                />
            </div>
        )}
        <span className="font-display text-worx text-sm">{s.n}</span>
        <h3 className="mt-2 font-display font-medium tracking-tight text-xl">{s.title}</h3>
        <p className="mt-2 text-sm text-paper/60 leading-relaxed">{s.desc}</p>
        <p className="mt-3 text-xs text-paper/40 italic">{s.outcome}</p>
        <a
            href="#booking"
            onClick={bookService(s.sid)}
            data-testid={`service-book-${s.n}`}
            className="mt-auto pt-6 inline-flex items-center gap-1.5 text-sm text-paper/70 group-hover:text-worx transition-colors duration-200 w-max"
        >
            Book this class <ArrowUpRight size={14} />
        </a>
    </article>
);

export const Services = () => (
    <section id="services" data-testid="services-section" className="bg-ink text-paper py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
            <Reveal>
                <p className="flex items-center gap-3 text-xs md:text-sm tracking-[0.25em] uppercase text-paper/50">
                    <span className="h-px w-10 bg-worx inline-block" /> What we do
                </p>
                <h2 className="mt-6 font-display font-semibold tracking-tighter leading-[0.95] text-5xl md:text-7xl max-w-4xl">
                    I'll build it for you — <span className="text-worx">or teach you how.</span>
                </h2>
                <p className="mt-6 text-paper/60 max-w-lg text-base md:text-lg leading-relaxed">
                    Every development project starts with a free one-hour discovery call — virtual on
                    Google Meet, at your place, or the Palm Beach County Main Library. We scope your build
                    together, then you get a straight quote.
                </p>
            </Reveal>

            <div className="mt-14 grid md:grid-cols-2 gap-px bg-paper/15 border border-paper/15">
                {DEV.map((s, i) => (
                    <Reveal key={s.n} delay={i * 0.08}>
                        <DevCard s={s} />
                    </Reveal>
                ))}
            </div>

            <Reveal delay={0.1}>
                <div id="classes" className="mt-20 flex flex-wrap items-end justify-between gap-4">
                    <h3 className="font-display font-medium tracking-tight text-3xl md:text-4xl">
                        Rather learn it yourself?
                    </h3>
                    <p className="text-paper/50 text-sm md:text-base">
                        1-on-1 AI classes — $95/hr prepaid online, $100 after class
                    </p>
                </div>
            </Reveal>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-paper/15 border border-paper/15">
                {CLASSES.map((s, i) => (
                    <Reveal key={s.n} delay={i * 0.08}>
                        <ClassCard s={s} />
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);
